/**
 * @fileoverview Pure helpers of the Task Tracker table: sub-column counting, initials and the per-lesson
 * aggregation of the per-activity assessment flags.
 */

import { aggregateLessonStatus, resolveTrackStatus } from '../utils/aggregateLessonStatus'
import type {
  StudentActivityStatus,
  TaskTrackActivity,
  TaskTrackLesson,
  TaskTrackStudentRow,
  TrackStatus,
} from '../types'

export function subColCount(lesson: TaskTrackLesson): number {
  return (lesson.hasAssessments ? 2 : 0) + lesson.deliverableActivities.length
}

export function initials(firstName: string, lastName: string): string {
  const firstInitial = firstName?.trim()?.[0] ?? ''
  const lastInitial = lastName?.trim()?.[0] ?? ''
  return `${firstInitial}${lastInitial}`.toUpperCase() || '?'
}

export type ActivitiesByLesson = Map<string, TaskTrackActivity[]>
/** activityId → studentId → cell. */
export type CellMap = Map<string, Map<string, StudentActivityStatus>>

export function buildActivitiesByLesson(activities: TaskTrackActivity[]): ActivitiesByLesson {
  const map: ActivitiesByLesson = new Map()
  for (const activity of activities) {
    const list = map.get(activity.lessonId) ?? []
    list.push(activity)
    map.set(activity.lessonId, list)
  }
  return map
}

export function buildCellMap(students: TaskTrackStudentRow[]): CellMap {
  const map: CellMap = new Map()
  for (const student of students) {
    for (const act of student.activities) {
      let activityCells = map.get(act.activityId)
      if (!activityCells) {
        activityCells = new Map()
        map.set(act.activityId, activityCells)
      }
      activityCells.set(student.id, act)
    }
  }
  return map
}

export interface LessonAssessmentStatus {
  selfStatus: TrackStatus
  teacherStatus: TrackStatus
}

export function aggregateLessonAssessmentStatus(
  lesson: TaskTrackLesson,
  studentActivities: StudentActivityStatus[],
  activitiesByLesson: ActivitiesByLesson,
): LessonAssessmentStatus {
  const lessonActivityIds = new Set((activitiesByLesson.get(lesson.id) ?? []).map(a => a.id))
  const relevant = studentActivities.filter(sa => lessonActivityIds.has(sa.activityId))
  return {
    selfStatus: aggregateLessonStatus(
      relevant.map(r => resolveTrackStatus(r.selfAssessmentStatus, r.selfAssessmentCompleted)),
    ),
    teacherStatus: aggregateLessonStatus(
      relevant.map(r => resolveTrackStatus(r.teacherAssessmentStatus, r.teacherAssessmentCompleted)),
    ),
  }
}
